// FILE: /src/Components/BuySubscription/BuySubscription.js (FULLY UPDATED & WARNINGS FIXED)

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { createSubscription, fetchUserProfile, getPlans, checkUserSubscriptionStatus } from "../services/user.profile";
import { LoadingOutlined, CloseOutlined, ArrowLeftOutlined, LogoutOutlined } from "@ant-design/icons";
import { message, Spin, Button, Switch, Collapse } from "antd";
import { useNavigate } from "react-router-dom";

// --- Helper: Feature Icon ---
const FeatureIcon = ({ included }) => (
<span
className={`select-plan-feature-icon ${
included
? 'select-plan-feature-icon-included'
: 'select-plan-feature-icon-unavailable'
}`}
aria-hidden="true"
>
<i className={`fa-solid ${included ? 'fa-check' : 'fa-minus'}`}></i>
</span>
);

// --- Helper: SocialChannelIcons ---
const SocialChannelIcons = ({ channels = [] }) => {
const iconMap = {
Facebook: 'fa-facebook',
Instagram: 'fa-instagram',
Pinterest: 'fa-pinterest',
X: 'fa-twitter',
LinkedIn: 'fa-linkedin',
YouTube: 'fa-youtube',
'Google Business': 'fa-google',
Tiktok: 'fa-tiktok'
};
if (channels.length === 0) return null;
return (
<div className="select-plan-social-channels" aria-label="Supported social channels">
{channels.map(channel => (
iconMap[channel] && (
<span key={channel} className="select-plan-social-channel" title={channel}>
<i className={`fa-brands ${iconMap[channel]}`}></i>
</span>
)
))}
</div>
);
};

// --- Child Component: Plan Card ---
const PlanCard = ({ plan, allPlans, onSelect, subscribingPlan, billingCycle, currentUserPlan }) => {
const [showMax, setShowMax] = useState(false);

const maxPlanVariant = useMemo(() =>
allPlans.find(p => p.base_plan_name === `${plan.base_plan_name} Max`),
[allPlans, plan.base_plan_name]
);

const isPopular = plan.base_plan_name === "Growth";
const isFree = plan.isFree;
const isCustom = plan.isCustom;

const displayData = useMemo(() => {
if (isFree || isCustom) return plan.monthly;
const targetPlan = showMax && maxPlanVariant ? maxPlanVariant : plan;
return billingCycle === 'yearly' && targetPlan.yearly ? targetPlan.yearly : targetPlan.monthly;
}, [plan, maxPlanVariant, billingCycle, isFree, isCustom, showMax]);

const isCurrentPlan = useMemo(() => {
if (!currentUserPlan || !displayData) return false;
if (isFree && currentUserPlan.plan_id === 'free_plan') return true;
return displayData.plan_id === currentUserPlan.plan_id;
}, [currentUserPlan, displayData, isFree]);

const discount = useMemo(() => {
if (isFree || isCustom || !plan.monthly || !plan.yearly) return 0;
const monthlyPrice = plan.monthly.plan_price;
const yearlyPricePerMonth = plan.yearly.plan_price / 12;
if (monthlyPrice === 0) return 0;
return Math.round(((monthlyPrice - yearlyPricePerMonth) / monthlyPrice) * 100);
}, [plan, isFree, isCustom]);

const convertInrToUsd = (inr, isPerMonth = true) => {
const usdPrice = (inr / 100) / 83;
return isPerMonth ? Math.round(usdPrice) : usdPrice.toFixed(2);
};

const getPriceDetails = () => {
const priceInPaise = displayData.plan_price || 0;
const isYearly = billingCycle === 'yearly';

const monthlyPriceInr = isYearly ? priceInPaise / 12 : priceInPaise;

const actualUsdPrice = convertInrToUsd(monthlyPriceInr, true);
const inflatedUsdPrice = actualUsdPrice * 10;

return {
actualUsdPrice,
inflatedUsdPrice,
billedAnnuallyUsd: convertInrToUsd(priceInPaise, false)
};
};

const priceDetails = getPriceDetails();

const getButtonText = () => {
if (isCurrentPlan) return "Current Plan";
if (isFree) return "Get Started";
if (isCustom) return "Contact Sales";
if (!currentUserPlan) return "Choose Plan";
const currentPrice = currentUserPlan.plan?.plan_price || 0;
const displayPrice = displayData.plan_price;
if (displayPrice > currentPrice) return "Upgrade";
if (displayPrice < currentPrice) return "Downgrade";
return "Choose Plan";
};

if (!displayData) return null;

return (
<article
className={`select-plan-card ${
isPopular ? 'select-plan-card-popular' : ''
} ${isCurrentPlan ? 'select-plan-card-current' : ''}`}
>
<div className="select-plan-card-status-row">
{isPopular && !isCurrentPlan && (
<span className="select-plan-status select-plan-status-popular">
Most popular
</span>
)}

{isCurrentPlan && (
<span className="select-plan-status select-plan-status-current">
Current plan
</span>
)}

{billingCycle === 'yearly' && !isFree && !isCustom && discount > 0 && (
<span className="select-plan-discount">
Save {discount}%
</span>
)}
</div>

<header className="select-plan-card-header">
<h3>{plan.base_plan_name}</h3>

{maxPlanVariant && (
<div className="select-plan-variant-control">
<span className={!showMax ? 'select-plan-variant-active' : ''}>
{plan.base_plan_name}
</span>
<Switch
checked={showMax}
onChange={setShowMax}
size="small"
aria-label={`Toggle ${plan.base_plan_name} Max`}
 />
<span className={showMax ? 'select-plan-variant-active' : ''}>
{plan.base_plan_name} Max
</span>
</div>
)}

<p>{displayData.plan_description}</p>
</header>

<div className="select-plan-price">
{isCustom ? (
<strong>Custom</strong>
) : isFree ? (
<strong>$0</strong>
) : (
<div className="select-plan-price-row">
<span className="select-plan-original-price">
${priceDetails.inflatedUsdPrice}
</span>
<strong>${priceDetails.actualUsdPrice}</strong>
<span className="select-plan-price-period">/ month</span>
</div>
)}

{billingCycle === 'yearly' && !isFree && !isCustom && (
<p>
Billed annually at ${priceDetails.billedAnnuallyUsd}
</p>
)}
</div>

<ul className="select-plan-features">
<li><FeatureIcon included={true} /><span>{displayData.brands_limit} Brand{displayData.brands_limit === 1 ? '' : 's'} included</span></li>
<li><FeatureIcon included={true} /><span>{displayData.posts_per_month_limit} scheduled posts per month</span></li>
<li><FeatureIcon included={true} /><span>Up to {displayData.profiles_limit} social channels</span></li>
<li><FeatureIcon included={true} /><span>{displayData.storage_gb_limit} GB asset library per brand</span></li>
<li><FeatureIcon included={true} /><span>{displayData.ai_credits_total_limit} AI credits per month</span></li>
<li><FeatureIcon included={displayData.reports_enabled === true} /><span>Profile and post reports</span></li>
<li><FeatureIcon included={displayData.seo_enabled === true} /><span>SEO content and publishing panel</span></li>
<li><FeatureIcon included={true} /><span>Support: {displayData.support_level || 'Community'}</span></li>
<li className="select-plan-channels-row">
<SocialChannelIcons channels={displayData.supported_channels} />
</li>
</ul>

<button
type="button"
onClick={() => {
if (isCustom) {
window.open('https://oolook.in/schedule-demo', '_blank');
} else {
onSelect(displayData.plan_id);
}
}}
disabled={!!subscribingPlan || isCurrentPlan}
className={`select-plan-action ${
isPopular ? 'select-plan-action-primary' : ''
} ${isCurrentPlan ? 'select-plan-action-current' : ''}`}
>
{subscribingPlan === displayData.plan_id ? (
<Spin
indicator={
<LoadingOutlined
style={{ fontSize: 18 }}
spin
/>
}
 />
) : (
getButtonText()
)}
</button>
</article>
);
};

// --- FAQ Section Component ---
const FAQSection = ({ freePlanEnabled }) => {
const faqData = [
{ key: '1', question: 'What counts as a social profile?', answer: 'Each social media account you connect to Oolook counts as one profile. For example, one Facebook Page, one Instagram account, and one Pinterest account would count as three social profiles.' },
{ key: '2', question: 'Can I change my plan later?', answer: 'Absolutely! You can upgrade, downgrade, or switch between monthly and yearly billing at any time from your account settings. Changes will be prorated.' },
{ key: '3', question: 'What happens if I cancel my subscription?', answer: freePlanEnabled ? 'Your paid plan remains active until the end of the current billing period. After that, your account moves to the Free Plan.' : 'Your paid plan remains active until the end of the current billing period. After that, you must select a new plan to continue using Oolook.' },
{ key: '4', question: 'Do you offer a free trial?', answer: freePlanEnabled ? 'You can select the Free Plan to explore Oolook without a paid subscription.' : 'A Free Plan is not currently available. App access begins after payment for a paid plan is confirmed.' },
{ key: '5', question: 'What payment methods do you accept?', answer: 'We accept all major credit cards, debit cards, UPI, and other popular payment methods through our secure payment partner, Razorpay.' },
{ key: '6', question: 'What are AI credits and how do they work?', answer: 'AI credits are used for our content generation features, like creating captions or images. Each plan comes with a monthly allowance of credits, which reset at the start of each billing cycle.' },
{ key: '7', question: 'Is my data safe with Oolook?', answer: 'Yes, data security is our top priority. We use industry-standard encryption for data in transit and at rest. All connections to social media platforms use official, secure APIs.' },
{ key: '8', question: 'Can I add multiple team members to my account?', answer: 'Team member functionality is available on our Pro and Enterprise plans, allowing you to collaborate seamlessly with your colleagues.' },
{ key: '9', question: 'What kind of support do you offer?', answer: 'Support levels vary by plan. All users have access to our help center. Paid plans include email and chat support, while our Enterprise plan offers a dedicated account manager.' },
{ key: '10', question: 'Do you have a plan for large agencies or enterprises?', answer: 'Yes! Our Enterprise plan is fully customizable to meet the needs of large teams and agencies. Please contact our sales team to discuss a bespoke solution.' },
];

const items = faqData.map(faq => ({
key: faq.key,
label: <span>{faq.question}</span>,
children: <p>{faq.answer}</p>,
className: "select-plan-faq-item"
}));

return (
<section className="select-plan-faq">
<header className="select-plan-faq-header">
<h2>Frequently asked questions</h2>
<p>Plan, billing, payment, and account details.</p>
</header>
<Collapse
items={items}
accordion
bordered={false}
className="select-plan-faq-collapse"
 />
</section>
);
};

// --- Main Subscription Component ---
export default function BuySubscription() {
const [userData, setUserData] = useState({});
const [allPlans, setAllPlans] = useState([]);
const [currentUserPlan, setCurrentUserPlan] = useState(null);
const [loading, setLoading] = useState(true);
const [isVerifying, setIsVerifying] = useState(false);
const [subscribingPlan, setSubscribingPlan] = useState(null);
const [subscriptionId, setSubscriptionId] = useState("");
const [billingCycle, setBillingCycle] = useState('monthly');
const [isChangingPlan, setIsChangingPlan] = useState(false);
const [freePlanEnabled, setFreePlanEnabled] = useState(false);
const navigate = useNavigate();

const handleLogout = useCallback(() => {
localStorage.clear();
sessionStorage.clear();
window.location.href = "/login";
}, []);

const initialize = useCallback(async () => {
try {
setLoading(true);
const { user, subscription } = await fetchUserProfile();
setUserData(user);

if (subscription && (subscription.status === 'active' || subscription.status === 'created')) {
setCurrentUserPlan(subscription);
if (subscription.status === 'active') {
setIsChangingPlan(true);
}
}

const plansResponse = await getPlans();
if (plansResponse.success) {
setAllPlans(plansResponse?.plans || []);
setFreePlanEnabled(plansResponse?.freePlanEnabled === true);
} else {
message.error("Failed to fetch subscription plans.");
}
} catch (error) {
message.error("Could not load user data or plans. Please try again.");
} finally {
setLoading(false);
}
}, []);

useEffect(() => {
initialize();
}, [initialize]);

const processedPlans = useMemo(() => {
    if (!allPlans || allPlans.length === 0) return [];

    // Separate the special plans
    const freePlan = allPlans.find(p => p.isFree);
    const enterprisePlan = allPlans.find(p => p.isCustom);
    
    // Get all other standard, paid plans
    const standardPlans = allPlans.filter(p => !p.isFree && !p.isCustom && !p.base_plan_name.endsWith(" Max"));

    // Sort the standard plans alphabetically by their base name
    standardPlans.sort((a, b) => a.base_plan_name.localeCompare(b.base_plan_name));
    
    // Construct the final sorted array
    const finalOrder = [];
    if (freePlan) finalOrder.push(freePlan);
    finalOrder.push(...standardPlans);
    if (enterprisePlan) finalOrder.push(enterprisePlan);

    return finalOrder;
}, [allPlans]);

const startPollingForSubscriptionStatus = useCallback(() => {
setIsVerifying(true);
let pollCount = 0;
const maxPolls = 15; 

const pollInterval = setInterval(async () => {
pollCount++;
try {
const status = await checkUserSubscriptionStatus();
if (status.isActivePlan && status.subscriptionStatus === 'active') {
clearInterval(pollInterval);
message.success("Subscription verified! Refreshing your account...");
window.location.href = isChangingPlan ? "/Dashboard/AccountSetting" : "/Dashboard";
} else if (pollCount >= maxPolls) {
clearInterval(pollInterval);
setIsVerifying(false);
message.warning(
"Payment confirmation is still pending. Please remain on this page and try again shortly.",
10
);
}
} catch (error) {
clearInterval(pollInterval);
setIsVerifying(false);
message.error(
"An error occurred during verification. Your app access will remain locked until payment is confirmed."
);
}
}, 2000); 
}, [navigate, isChangingPlan]);

const handleSubBuy = async (plan_id) => {
    if (!userData?.email_id) {
        message.error("User data not loaded. Please refresh the page.");
        return;
    }

    if (plan_id === "free_plan") {
        setSubscribingPlan('free_plan');
        try {
            const data = await createSubscription({
                email_id: userData.email_id,
                plan_id: 'free_plan',
                razorpay_customer_id: userData.razorpay_customer_id,
            });

            if (data?.cancellationScheduled) {
                message.success(data.message);
                setTimeout(() => {
                    window.location.href = "/Dashboard/AccountSetting";
                }, 1500);
            } else if (data?.success) {
                message.success("Successfully changed to Free Plan! Refreshing your account...");
                setTimeout(() => {
                    window.location.href = isChangingPlan ? "/Dashboard/AccountSetting" : "/Dashboard";
                }, 1500);
            }
        } catch (error) {
            message.error(error.message || "Failed to change plan.");
        } finally {
            setSubscribingPlan(null);
        }
        return;
    }

    setSubscribingPlan(plan_id);
    try {
        const data = await createSubscription({
            email_id: userData.email_id,
            firstName: userData.first_name,
            contact: userData.mobile,
            razorpay_customer_id: userData.razorpay_customer_id,
            plan_id,
        });
        
        if (data?.subscription?.id) {
            setSubscriptionId(data.subscription.id);
        } else {
            throw new Error(data?.error || "Subscription failed.");
        }
    } catch (error) {
        const errorMessage = error.response?.data?.error || "Unable to subscribe. Please try again.";
        message.error(errorMessage);
    } finally {
        setSubscribingPlan(null);
    }
};

const handlePayment = useCallback(async () => {
const options = {
key: process.env.REACT_APP_RAZORPAY_KEY_ID,
subscription_id: subscriptionId,
name: "Oolook",
description: "Subscription",
handler: async () => {
startPollingForSubscriptionStatus();
},
prefill: { email: userData.email_id, contact: userData.mobile },
theme: { color: "#6D28D9" },
};
const rzp = new window.Razorpay(options);
rzp.open();
}, [subscriptionId, userData, startPollingForSubscriptionStatus]);

useEffect(() => {
if (subscriptionId) {
handlePayment();
}
}, [subscriptionId, handlePayment]);

return (
<div className="select-plan-page">
{isVerifying && (
<div className="select-plan-verification-overlay" role="status" aria-live="polite">
<div className="select-plan-verification-card">
<span className="select-plan-verification-icon" aria-hidden="true">
<i className="fa-solid fa-shield-halved"></i>
</span>
<Spin size="large" />
<h2>Verifying your payment</h2>
<p>
This may take a few moments. Your account will refresh automatically.
</p>
</div>
</div>
)}

<nav className="select-plan-navigation" aria-label="Plan selection navigation">
{isChangingPlan ? (
<>
<Button
icon={<ArrowLeftOutlined />}
onClick={() => navigate(-1)}
className="select-plan-secondary-button"
>
Back to billing
</Button>
<Button
type="text"
icon={<CloseOutlined />}
onClick={() => navigate('/Dashboard/AccountSetting')}
className="select-plan-close-button"
aria-label="Close plan selection"
 />
</>
) : (
<Button
icon={<LogoutOutlined />}
onClick={handleLogout}
className="select-plan-secondary-button"
>
Log out
</Button>
)}
</nav>

{loading ? (
<div className="select-plan-loading" role="status">
<Spin size="large" />
<p>Loading available plans…</p>
</div>
) : (
<main className="select-plan-content">
<header className="select-plan-page-header">
<span className="select-plan-eyebrow">
Plans and billing
</span>
<h1>{isChangingPlan ? 'Change your plan' : 'Choose your plan'}</h1>
<p>
Select the plan that fits your current publishing, storage, and AI usage.
</p>
</header>

<div className="select-plan-billing-control" aria-label="Billing cycle">
<button
type="button"
onClick={() => setBillingCycle('monthly')}
className={billingCycle === 'monthly' ? 'select-plan-billing-active' : ''}
aria-pressed={billingCycle === 'monthly'}
>
Monthly
</button>
<button
type="button"
onClick={() => setBillingCycle('yearly')}
className={billingCycle === 'yearly' ? 'select-plan-billing-active' : ''}
aria-pressed={billingCycle === 'yearly'}
>
Yearly
</button>
</div>

<section className="select-plan-grid" aria-label="Available subscription plans">
{processedPlans.map((plan) => (
<PlanCard
key={plan.base_plan_name}
plan={plan}
onSelect={handleSubBuy}
subscribingPlan={subscribingPlan}
billingCycle={billingCycle}
currentUserPlan={currentUserPlan}
allPlans={allPlans}
/>
))}
</section>

<FAQSection freePlanEnabled={freePlanEnabled} />
</main>
)}
</div>
);
}